import React, { useMemo, useState, useEffect } from "react";
import { Chart, Coordinate, Interval, Legend, Tooltip, Axis } from "bizcharts";
import { apiWrapWithErrorWithData } from "../../utils/apiWrapper";
import customAxios from "../../Redux/Actions/axios";
import CONFIG from "../../config";
import Loader from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import "./index.css";
import { getJobStatistics } from "../../Redux/Actions/jobs";  

const MOCK_JOBS = [
  {
    id: 10319,
    companyRefName: "APSAL-10319",
    jobStatus: "Cancelled",
    createdAt: "12-12-2025 08.37",
    jobCount: 1,
  },
  {
    id: 10320,
    companyRefName: "APSAL-10320",
    jobStatus: "In Progress",
    createdAt: "12-12-2025 10.35",
    jobCount: 2,
  },
];

// Status colors (labels will be translated dynamically)
const STATUS_COLORS = {
  awaitingpayment: "#d9534f",
  pending: "#f0a500",
  cancelled: "#6c757d",
  completed: "#1f8b4c",
  draft: "#adb5bd",
};

const normalizeStatusKey = (value) => {
  if (!value) return "pending";
  return String(value).trim().toLowerCase().replace(/[\s_-]+/g, "");
};

const parseApiDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value).trim();

  // "DD-MM-YYYY HH.mm" or "DD-MM-YYYY HH:mm"
  let m = raw.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2})[.:](\d{2}))?$/
  );
  if (m) {
    const [, dd, mm, yyyy, HH = "00", Min = "00"] = m;
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(HH),
      Number(Min),
      0,
      0
    );
  }

  // "YYYY-MM-DD" (from date input)
  m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [, yyyy, mm, dd] = m;
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      0,
      0,
      0,
      0
    );
  }

  return null;
};

const normalizeDate = (value) => {
  const parsed = parseApiDate(value);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const getRange = (key, customRange) => {
  const today = new Date();
  const startToday = startOfDay(today);
  const endToday = endOfDay(today);

  if (key === "today") return { start: startToday, end: endToday };

  if (key === "yesterday") {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
  }

  if (key === "thisMonth") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    return { start, end };
  }

  if (key === "lastMonth") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = endOfDay(new Date(today.getFullYear(), today.getMonth(), 0));
    return { start, end };
  }

  if (key === "custom" && customRange.from && customRange.to) {
    const startDate = normalizeDate(customRange.from);
    const endDate = normalizeDate(customRange.to);
    if (startDate && endDate) {
      return {
        start: startOfDay(startDate),
        end: endOfDay(endDate),
      };
    }
  }

  return { start: null, end: null };
};

function Home() {
  const { t } = useTranslation();
  const [selectedRange, setSelectedRange] = useState("today");
  const [customRange, setCustomRange] = useState({
    from: "",
    to: "",
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Range options with translations
  const RANGE_OPTIONS = useMemo(
    () => [
      { key: "today", label: t("dashboard.rangeButtons.today") },
      { key: "yesterday", label: t("dashboard.rangeButtons.yesterday") },
      { key: "thisMonth", label: t("dashboard.rangeButtons.thisMonth") },
      { key: "lastMonth", label: t("dashboard.rangeButtons.lastMonth") },
      { key: "custom", label: t("dashboard.rangeButtons.customRange") },
    ],
    [t]
  );

  // Status metadata with translations
  const STATUS_META = useMemo(
    () => ({
      draft: {
        label: t("dashboard.statusLabels.draft"),
        color: STATUS_COLORS.draft,
      },
      awaitingpayment: {
        label: t("dashboard.statusLabels.awaitingPayment"),
        color: STATUS_COLORS.awaitingpayment,
      },
      pending: {
        label: t("dashboard.statusLabels.pending"),
        color: STATUS_COLORS.pending,
      },
      cancelled: {
        label: t("dashboard.statusLabels.cancelled"),
        color: STATUS_COLORS.cancelled,
      },
      completed: {
        label: t("dashboard.statusLabels.completed"),
        color: STATUS_COLORS.completed,
      },
    }),
    [t]
  );

  const { start, end } = useMemo(
    () => getRange(selectedRange, customRange),
    [selectedRange, customRange]
  );

  useEffect(() => {
    if (!start || !end) {
      setJobs([]);
      return;
    }

    const fetchJobStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const startDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
        const endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;

        const res = await getJobStatistics(startDate, endDate);
        console.log("API Response:", res);
        // API response:
        // { message: "Success", data: { jobs: [...], totalRecords: n } }
        const list = res?.data?.jobs;
        setJobs(Array.isArray(list) ? list : []);
      } catch (e) {
        setError(t("dashboard.error"));
        setJobs(MOCK_JOBS);
      } finally {
        setLoading(false);
      }
    };

    fetchJobStatistics();
  }, [start, end]);

  const filteredJobs = useMemo(() => {
    if (!start || !end) return [];
    // API should already filter, but keep a defensive client-side filter too.
    return jobs.filter((job) => {
      const dateValue =
        job.createdAt ||
        job.date ||
        job.createdDate ||
        job.jobDate ||
        job.dateCreated;
      const jobDate = normalizeDate(dateValue);
      console.log("Filtering job", job.id, "with date", jobDate);
      if (!jobDate) return false;
      return jobDate >= start && jobDate <= end;
    });
  }, [jobs, start, end]);

  const stats = useMemo(() => {
    const initialStats = {
      awaitingpayment: 0,
      pending: 0,
      cancelled: 0,
      completed: 0,
      draft: 0,
      total: 0,
    };
    return filteredJobs.reduce(
      (acc, job) => {
        const statusValue =
          job.jobStatus || job.status || job.jobStatusType || job.statusType || "Pending";
        const key = normalizeStatusKey(statusValue);

        if (acc.hasOwnProperty(key)) {
          acc[key] = (acc[key] || 0) + 1;
        }
        acc.total += 1;
        return acc;
      },
      initialStats
    );
  }, [filteredJobs]);

  const chartData = useMemo(() => {
    return Object.keys(STATUS_META).map((statusKey) => ({
      type: STATUS_META[statusKey].label,
      value: stats[statusKey] || 0,
      key: statusKey,
    }));
  }, [stats]);

  const handleCustomChange = (key, value) => {
    setCustomRange((prev) => ({ ...prev, [key]: value }));
  };

  const customValid = customRange.from && customRange.to;

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">{t("dashboard.title")}</h1>
          <p className="dashboard-subtitle">{t("dashboard.subtitle")}</p>
        </div>

        <div className="range-buttons">
          {RANGE_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              className={`range-button ${
                selectedRange === key ? "range-button--active" : ""
              }`}
              onClick={() => setSelectedRange(key)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {selectedRange === "custom" && (
        <div className="custom-range">
          <label className="custom-range__field">
            <span>{t("dashboard.customRange.from")}</span>
            <input
              type="date"
              value={customRange.from}
              onChange={(e) => handleCustomChange("from", e.target.value)}
            />
          </label>
          <label className="custom-range__field">
            <span>{t("dashboard.customRange.to")}</span>
            <input
              type="date"
              value={customRange.to}
              min={customRange.from || undefined}
              onChange={(e) => handleCustomChange("to", e.target.value)}
            />
          </label>
          {!customValid && (
            <div className="custom-range__hint">
              {t("dashboard.customRange.hint")}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Loader type="ThreeDots" color="#121180" height={50} width={50} />
          <p style={{ marginTop: "16px", color: "#6c757d" }}>
            {t("dashboard.loading")}
          </p>
        </div>
      )}

      {error && (
        <div
          style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            color: "#856404",
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}

      {!loading && (
        <>
      <div className="summary-grid">
        {Object.keys(STATUS_META).map((statusKey) => {
          const meta = STATUS_META[statusKey];
          return (
            <div
              key={statusKey}
              className="summary-card"
              style={{ borderTopColor: meta.color }}
            >
              <div className="summary-card__label">{meta.label}</div>
              <div className="summary-card__value">
                {stats[statusKey] || 0}
              </div>
            </div>
          );
        })}
        <div className="summary-card summary-card--muted">
          <div className="summary-card__label">{t("dashboard.totalJobs")}</div>
          <div className="summary-card__value">{stats.total || 0}</div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-card__header">
            <h3>{t("dashboard.charts.statusSplit.title")}</h3>
            <p className="chart-card__sub">
              {t("dashboard.charts.statusSplit.subtitle")}
            </p>
          </div>

          <div className="chart-card__body">
            {stats.total === 0 ? (
              <div className="empty-state">{t("dashboard.charts.noData")}</div>
            ) : (
              <Chart
                autoFit
                height={260}
                data={chartData.filter((d) => d.value > 0)}
                padding={[10, 10, 10, 10]}
              >
                <Coordinate type="theta" radius={0.9} innerRadius={0.55} />
                <Tooltip showTitle={false} />
                <Legend position="bottom" />
                <Interval
                  position="value"
                  adjust="stack"
                  color={[
                    "key",
                    (key) => {
                      return STATUS_META[key]?.color || "#6c757d";
                    },
                  ]}
                  style={{
                    lineWidth: 1,
                    stroke: "#fff",
                  }}
                  tooltip={[
                    "type*value",
                    (type, value) => ({
                      name: type,
                      value,
                    }),
                  ]}
                />
              </Chart>
            )}
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-card__header">
            <h3>{t("dashboard.charts.statusDistribution.title")}</h3>
            <p className="chart-card__sub">
              {t("dashboard.charts.statusDistribution.subtitle")}
            </p>
          </div>

          <div className="chart-card__body">
            {stats.total === 0 ? (
              <div className="empty-state">{t("dashboard.charts.noData")}</div>
            ) : (
              <Chart
                autoFit
                height={260}
                data={chartData.filter((d) => d.value > 0)}
                padding={[20, 20, 40, 40]}
              >
                <Axis name="type" />
                <Axis name="value" />
                <Tooltip />
                <Interval
                  position="type*value"
                  color={[
                    "key",
                    (key) => {
                      return STATUS_META[key]?.color || "#6c757d";
                    },
                  ]}
                  style={{
                    radius: [4, 4, 0, 0],
                  }}
                  tooltip={[
                    "type*value",
                    (type, value) => ({
                      name: type,
                      value,
                    }),
                  ]}
                />
              </Chart>
            )}
          </div>
        </div>
      </div>

      <div className="jobs-preview">
        <div className="jobs-preview__header">
          <div>
            <h3>{t("dashboard.jobsPreview.title")}</h3>
            <p className="jobs-preview__count">
              {t("dashboard.jobsPreview.showing")} {filteredJobs.length}{" "}
              {filteredJobs.length !== 1
                ? t("dashboard.jobsPreview.records")
                : t("dashboard.jobsPreview.record")}
            </p>
          </div>
          {start && end && (
            <div className="jobs-preview__range">
              {start.toLocaleDateString()} – {end.toLocaleDateString()}
            </div>
          )}
        </div>

        {filteredJobs.length === 0 ? (
          <div className="empty-state">
            {t("dashboard.jobsPreview.noJobs")}
          </div>
        ) : (
          <div className="jobs-list">
            {filteredJobs.map((job) => {
              const jobId = job.id;
              const title = job.companyRefName || job.title || job.jobTitle || job.name || "Job";
              const statusValue =
                job.jobStatus || job.status || job.jobStatusType || job.statusType || "Pending";
              const statusKey = normalizeStatusKey(statusValue);
              const createdAt =
                job.createdAt || job.date || job.createdDate || job.jobDate || job.dateCreated;
              const parsed = parseApiDate(createdAt);

              return (
                <div key={String(jobId)} className="job-row">
                  <div>
                    <div className="job-row__title">{title}</div>
                    <div className="job-row__meta">
                      {String(jobId)}
                      {" • "}
                      {parsed ? parsed.toLocaleString() : "—"}
                      {job.jobCount != null
                        ? ` • ${t("dashboard.jobsPreview.count")}: ${job.jobCount}`
                        : ""}
                    </div>
                  </div>
                  <span
                    className="job-row__badge"
                    style={{
                      backgroundColor: STATUS_META[statusKey]?.color || "#6c757d",
                    }}
                  >
                    {STATUS_META[statusKey]?.label || String(statusValue)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}

export default Home;